import { inject, Injectable } from '@angular/core';
import { Bookmark } from '../models/bookmark';
import {
  BookmarkValidationIssue,
  BookmarkValidationResult,
  BookmarkValidationSummary,
  isWarningIssue,
} from '../models/bookmark-validation';
import { CustomGridStackWidget } from './grid-widget-config.service';
import { IndicatorDataStore } from '../stores/indicatpr-data/indicator-data.state';
import { DataLayerStore } from '../stores/data-layer/data-layer.state';
import { IndicatorStore } from '../stores/indicator/indicator.state';
import { IndicatorData } from '../models/indicator-data';
import { DataLayer } from '../models/data-layer';
import { Indicator } from '../models/indicator';
import { ActiveUserStore } from '../stores/user/active-user.state';
import { BookmarkStore } from '../stores/bookmark/bookmark.state';
import { DataLayerTypeStore } from '../stores/data-layer-type/data-layer-type.state';
import { isObjectId } from '../utility/app.utility';
import { BookmarkService } from './bookmark/bookmark.service';
import { ConfirmationDialogService } from './confirmation-dialog.service';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { TopicStore } from '../stores/topic/topic.state';

@Injectable({
  providedIn: 'root',
})
export class BookmarkValidationService {
  private readonly indicatorDataStore = inject(IndicatorDataStore);
  private readonly dataLayerStore = inject(DataLayerStore);
  private readonly dataLayerTypeStore = inject(DataLayerTypeStore);
  private readonly indicatorStore = inject(IndicatorStore);
  private readonly bookmarkStore = inject(BookmarkStore);
  private readonly topicStore = inject(TopicStore);
  private readonly activeUserStore = inject(ActiveUserStore);
  private readonly bookmarkService = inject(BookmarkService);
  private readonly confirmationDialogService = inject(
    ConfirmationDialogService
  );
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  /** views already checked in the internal atlas (one hint per view) */
  private readonly notifiedBookmarkIds = new Set<string>();

  /**
   * Opens the overlay "Nutzung & Freigabe" of a view in the data backend -
   * everything the view uses and what publishing it still needs.
   *
   * @param bookmarkId The view
   */
  openInventory(bookmarkId: string): void {
    this.router.navigate([
      '/data-backend',
      {
        outlets: {
          overlay: [
            'lesezeichen',
            { outlets: { bearbeiten: ['nutzung', bookmarkId] } },
          ],
        },
      },
    ]);
  }

  /**
   * Validates a single bookmark for publication issues
   * @param bookmark The bookmark to validate
   * @returns Validation result with all found issues
   */
  validateBookmark(bookmark: Bookmark): BookmarkValidationResult {
    const issues: BookmarkValidationIssue[] = [];

    // Only validate public bookmarks (private bookmarks don't need validation)
    if (bookmark.private || !bookmark.published) {
      return {
        bookmarkId: bookmark._id,
        bookmarkTitle: bookmark.title,
        isValid: true,
        issues: [],
        validatedAt: new Date(),
      };
    }

    // Check each widget in the bookmark configuration
    if (bookmark.config && Array.isArray(bookmark.config)) {
      bookmark.config.forEach((widget: CustomGridStackWidget) => {
        const widgetIssues = this.validateWidget(widget, bookmark);
        issues.push(...widgetIssues);
      });
    }

    return {
      bookmarkId: bookmark._id,
      bookmarkTitle: bookmark.title,
      isValid: issues.length === 0,
      issues,
      validatedAt: new Date(),
    };
  }

  /**
   * Validates a single widget for publication issues
   * @param widget The widget to validate
   * @param bookmark The parent bookmark
   * @returns Array of validation issues found
   */
  private validateWidget(
    widget: CustomGridStackWidget,
    bookmark: Bookmark
  ): BookmarkValidationIssue[] {
    const issues: BookmarkValidationIssue[] = [];

    // Skip content widgets (text, teaser, info)
    if (!widget.widget || ['text', 'teaser', 'info'].includes(widget.type)) {
      return issues;
    }

    const widgetConfig = widget.widget;

    // Get all data layers from widget
    const dataLayerIds = this.getDataLayerIdsFromWidget(widgetConfig);

    // Get all indicators from widget
    const indicatorIds = this.getIndicatorIdsFromWidget(widgetConfig);

    // Check each combination of dataLayer and indicator
    dataLayerIds.forEach(dataLayerId => {
      const dataLayer = this.dataLayerStore.getById(dataLayerId);

      if (!dataLayer) {
        // DataLayer not found - create missing-datalayer issue
        issues.push(
          this.createIssue(
            bookmark,
            widget,
            'missing-datalayer',
            undefined,
            undefined,
            undefined,
            dataLayerId
          )
        );
        return; // Skip further checks for this dataLayer
      }

      // Check if dataLayer is published
      if (!dataLayer.published) {
        issues.push(
          this.createIssue(
            bookmark,
            widget,
            'unpublished-datalayer',
            dataLayer,
            undefined,
            undefined
          )
        );
      }

      indicatorIds.forEach(indicatorId => {
        const indicator = this.indicatorStore.getById(indicatorId);

        if (!indicator) {
          // Indicator not found - create missing-indicator issue
          issues.push(
            this.createIssue(
              bookmark,
              widget,
              'missing-indicator',
              dataLayer,
              undefined,
              undefined,
              indicatorId
            )
          );
          return; // Skip further checks for this indicator
        }

        // Find the IndicatorData for this combination
        const indicatorData =
          this.indicatorDataStore.getByDataLayerAndIndicator(
            dataLayerId,
            indicatorId
          );

        if (indicatorData) {
          // Check if IndicatorData is published
          if (!indicatorData.published) {
            issues.push(
              this.createIssue(
                bookmark,
                widget,
                'unpublished-data',
                dataLayer,
                indicator,
                indicatorData
              )
            );
          }
        }
      });
    });

    return issues;
  }

  /**
   * Extracts all dataLayer IDs from a widget configuration
   * @param widgetConfig The widget configuration
   * @returns Array of dataLayer IDs
   */
  private getDataLayerIdsFromWidget(widgetConfig: any): string[] {
    const ids: string[] = [];

    // Single dataLayer
    if (widgetConfig.dataLayer) {
      ids.push(widgetConfig.dataLayer);
    }

    // Multiple dataLayers
    if (
      widgetConfig.dataLayers &&
      Array.isArray(widgetConfig.dataLayers) &&
      widgetConfig.dataLayers.length > 0
    ) {
      ids.push(...widgetConfig.dataLayers);
    }

    return [...new Set(ids)]; // Remove duplicates
  }

  /**
   * Extracts all indicator IDs from a widget configuration
   * @param widgetConfig The widget configuration
   * @returns Array of indicator IDs
   */
  private getIndicatorIdsFromWidget(widgetConfig: any): string[] {
    const ids: string[] = [];

    // Single indicator
    if (widgetConfig.indicator) {
      ids.push(widgetConfig.indicator);
    }

    // Multiple indicators
    if (
      widgetConfig.indicators &&
      Array.isArray(widgetConfig.indicators) &&
      widgetConfig.indicators.length > 0
    ) {
      ids.push(...widgetConfig.indicators);
    }

    // Axis properties for scatter/bubble charts
    if (
      widgetConfig.axisProperties &&
      Array.isArray(widgetConfig.axisProperties)
    ) {
      ids.push(...widgetConfig.axisProperties);
    }

    return [...new Set(ids.filter(id => isObjectId(id)))]; // Remove duplicates
  }

  /**
   * Creates a validation issue object
   */
  private createIssue(
    bookmark: Bookmark,
    widget: CustomGridStackWidget,
    issueType: BookmarkValidationIssue['issueType'],
    dataLayer?: DataLayer,
    indicator?: Indicator,
    indicatorData?: IndicatorData,
    missingId?: string
  ): BookmarkValidationIssue {
    let message = '';

    const dataLayerType = dataLayer
      ? this.dataLayerTypeStore.getById(dataLayer.parent)
      : null;

    switch (issueType) {
      case 'unpublished-datalayer':
        message = `Jahr "${dataLayer?.year} (${dataLayerType?.name || 'Raum unbekannt'})" ist nicht veröffentlicht`;
        break;
      case 'unpublished-indicator':
        message = `Indikator "${indicator?.name}" ist nicht veröffentlicht`;
        break;
      case 'unpublished-data':
        message = `Daten für "${indicator?.name}" im Jahr "${dataLayer?.year} (${dataLayerType?.name || 'Raum unbekannt'})" sind nicht veröffentlicht`;
        break;
      case 'missing-datalayer':
        message = `Jahr mit ID "${missingId}" wurde nicht gefunden`;
        break;
      case 'missing-indicator':
        message = `Indikator mit ID "${missingId}" wurde nicht gefunden`;
        break;
    }

    // Generate widget title
    const widgetTitle = this.getWidgetTitle(widget, indicator);

    return {
      bookmarkId: bookmark._id,
      bookmarkTitle: bookmark.title,
      issueType,
      widgetId: String(widget.id),
      widgetType: widget.type,
      widgetHeadline: widgetTitle,
      details: {
        dataLayerId: dataLayer?._id || missingId,
        dataLayerName: dataLayer?.year ? String(dataLayer.year) : missingId,
        indicatorId: indicator?._id || missingId,
        indicatorName: indicator?.name || missingId,
        indicatorDataId: indicatorData?._id,
      },
      message,
    };
  }

  /**
   * Gets a descriptive title for a widget
   * Priority: 1. Custom headline, 2. Indicator name, 3. Widget type
   */
  private getWidgetTitle(
    widget: CustomGridStackWidget,
    indicator?: Indicator
  ): string {
    // First priority: custom headline
    if (widget.widget?.headline) {
      return widget.widget.headline;
    }

    // Second priority: indicator name
    if (indicator?.name) {
      return indicator.name;
    }

    // Third priority: widget type with multiple indicators
    if (widget.widget?.indicators && widget.widget.indicators.length > 0) {
      const firstIndicator = this.indicatorStore.getById(
        widget.widget.indicators[0]
      );
      if (firstIndicator) {
        return `${firstIndicator.name} (+${widget.widget.indicators.length - 1})`;
      }
    }

    // Fallback: widget type label
    return this.getWidgetTypeLabel(widget.type);
  }

  /**
   * Gets a human-readable label for widget type
   */
  private getWidgetTypeLabel(type: string): string {
    const labels: { [key: string]: string } = {
      map: 'Karte',
      bar: 'Balken-/Säulendiagramm',
      line: 'Liniendiagramm',
      pie: 'Kreis-/Ringdiagramm',
      scatter: 'Streudiagramm',
      bubble: 'Blasendiagramm',
      radar: 'Netz-/Radardiagramm',
      polarArea: 'Polardiagramm',
      table: 'Tabelle',
      text: 'Text',
      info: 'Indikatorinfo',
      teaser: 'Teaser/Bild',
    };

    return labels[type] || type;
  }

  /**
   * Validates multiple bookmarks
   * @param bookmarks Array of bookmarks to validate
   * @returns Summary of validation results
   */
  validateBookmarks(bookmarks: Bookmark[]): BookmarkValidationSummary {
    const results = bookmarks.map(bookmark => this.validateBookmark(bookmark));

    const validBookmarks = results.filter(r => r.isValid).length;
    const invalidBookmarks = results.filter(r => !r.isValid).length;
    const totalIssues = results.reduce((sum, r) => sum + r.issues.length, 0);

    return {
      totalBookmarks: bookmarks.length,
      validBookmarks,
      invalidBookmarks,
      totalIssues,
      results,
      validatedAt: new Date(),
    };
  }

  /**
   * Validates only public bookmarks
   * @param bookmarks Array of all bookmarks
   * @returns Summary of validation results for public bookmarks only
   */
  validatePublicBookmarks(bookmarks: Bookmark[]): BookmarkValidationSummary {
    const publicBookmarks = bookmarks.filter(b => !b.private && b.published);
    return this.validateBookmarks(publicBookmarks);
  }

  /**
   * Validates access permissions for all bookmarks based on user's workspace
   * @param bookmarks Array of all bookmarks
   * @returns Summary with both public validation AND access validation AND data validation
   */
  validateBookmarksWithAccess(
    bookmarks?: Bookmark[]
  ): BookmarkValidationSummary {
    const bookmarksToValidate = bookmarks || this.bookmarkStore.asArray();
    // Get public validation (existing functionality)
    const publicValidation = this.validatePublicBookmarks(bookmarksToValidate);

    // Get access validation (new functionality)
    const accessResults = bookmarksToValidate.map(bookmark =>
      this.validateBookmarkAccess(bookmark)
    );

    const validAccessBookmarks = accessResults.filter(r => r.isValid).length;
    const invalidAccessBookmarks = accessResults.filter(r => !r.isValid).length;
    const totalAccessIssues = accessResults.reduce(
      (sum, r) => sum + r.issues.length,
      0
    );

    // Get data availability validation (checks ALL bookmarks)
    const dataResults = bookmarksToValidate.map(bookmark =>
      this.validateBookmarkDataAvailability(bookmark)
    );

    const validDataBookmarks = dataResults.filter(r => r.isValid).length;
    const invalidDataBookmarks = dataResults.filter(r => !r.isValid).length;
    const totalDataIssues = dataResults.reduce(
      (sum, r) => sum + r.issues.length,
      0
    );

    // Annotate issues caused by access restrictions (both public and data validation)
    const annotatedPublicResults = this.markIssuesCausedByAccess(
      publicValidation.results,
      accessResults
    );
    const annotatedDataResults = this.markIssuesCausedByAccess(
      dataResults,
      accessResults
    );

    return {
      ...publicValidation,
      results: annotatedPublicResults,
      accessValidation: {
        totalBookmarks: bookmarksToValidate.length,
        validBookmarks: validAccessBookmarks,
        invalidBookmarks: invalidAccessBookmarks,
        totalIssues: totalAccessIssues,
        results: accessResults,
      },
      dataValidation: {
        totalBookmarks: bookmarksToValidate.length,
        validBookmarks: validDataBookmarks,
        invalidBookmarks: invalidDataBookmarks,
        totalIssues: totalDataIssues,
        results: annotatedDataResults,
      },
    };
  }

  /**
   * In the internal atlas: tells the user once per view when some of its
   * widgets stay empty because referenced indicators/years were deleted or
   * are missing, or no data exists - instead of silently empty widgets.
   * Unpublished content is not reported here (it is visible internally).
   *
   * @param bookmarkId The view that was just opened
   */
  async notifyIfIncomplete(bookmarkId: string): Promise<void> {
    if (
      this.activeUserStore.context() !== 'atlasInternal' ||
      !this.activeUserStore.isLoggedIn() ||
      this.notifiedBookmarkIds.has(bookmarkId)
    ) {
      return;
    }
    this.notifiedBookmarkIds.add(bookmarkId);

    let result: BookmarkValidationResult;
    try {
      result = await this.bookmarkService.validateOne(bookmarkId);
    } catch (error) {
      return; // a hint only - never disturb opening the view
    }
    const affectedWidgets = new Set(
      result.issues
        .filter(
          issue =>
            issue.severity === 'danger' ||
            issue.issueType === 'no-data-available'
        )
        .map(issue => issue.widgetId)
    ).size;
    if (affectedWidgets === 0) return;

    this.messageService.add({
      severity: 'warn',
      summary: 'Ansicht unvollständig',
      detail: `${affectedWidgets} Widget${affectedWidgets === 1 ? ' zeigt' : 's zeigen'} keine oder unvollständige Daten (gelöschte oder fehlende Indikatoren/Jahre bzw. keine Daten). Details: Datenpflege → Ansichten → Validierung.`,
      life: 10000,
    });
  }

  /**
   * Like confirmPublication, for several views published together with other
   * entities (publish manager): problems the same batch resolves - e.g. the
   * view's unpublished year is part of the selection - don't count.
   *
   * @param bookmarks Views about to be published
   * @param publishedAlong Ids of years, indicator data and other references
   *   (topics, reports, views) published in the same batch
   * @returns 'clean' if nothing to warn about, otherwise the user's choice
   */
  async confirmBatchPublication(
    bookmarks: Bookmark[],
    publishedAlong: {
      dataLayerIds: string[];
      indicatorDataIds: string[];
      referenceIds: string[];
    }
  ): Promise<'clean' | 'confirmed' | 'cancelled'> {
    if (bookmarks.length === 0) return 'clean';
    const resolved = new Set([
      ...publishedAlong.dataLayerIds,
      ...publishedAlong.indicatorDataIds,
      ...publishedAlong.referenceIds,
      ...bookmarks.map(bookmark => bookmark._id),
    ]);
    const isResolvedByBatch = (issue: BookmarkValidationIssue) =>
      (issue.issueType === 'unpublished-datalayer' &&
        resolved.has(issue.details.dataLayerId || '')) ||
      (issue.issueType === 'unpublished-data' &&
        resolved.has(issue.details.indicatorDataId || '')) ||
      (issue.issueType === 'unpublished-reference' &&
        resolved.has(issue.details.referenceId || ''));

    let results: BookmarkValidationResult[];
    try {
      results = await Promise.all(
        bookmarks.map(bookmark =>
          this.bookmarkService.validateDraft({
            ...bookmark,
            published: true,
            private: false,
          })
        )
      );
    } catch (error) {
      console.error('Validation before publishing failed', error);
      return 'clean'; // never block publishing on a failing validation
    }

    const entries = results.flatMap((result, index) =>
      result.issues
        .filter(issue => issue.severity !== 'info' && !isResolvedByBatch(issue))
        .map(issue => ({
          bookmarkId: bookmarks[index]._id,
          text: `${bookmarks[index].title}: ${issue.message}`,
        }))
    );
    if (entries.length === 0) return 'clean';

    const problems = entries.map(entry => entry.text);
    const affectedViews = new Set(entries.map(entry => entry.bookmarkId)).size;
    const uniqueProblems = [...new Set(problems)];
    const confirmed =
      await this.confirmationDialogService.confirmPublishWithIssues(
        'Auswahl',
        uniqueProblems,
        `${affectedViews} Ansicht${affectedViews === 1 ? '' : 'en'} der Auswahl ${affectedViews === 1 ? 'hat' : 'haben'} ${uniqueProblems.length} Problem${uniqueProblems.length === 1 ? '' : 'e'}`
      );
    return confirmed ? 'confirmed' : 'cancelled';
  }

  /**
   * Public views (published, not private) with a widget showing any of the
   * given indicator data - they'd lose data if it gets unpublished.
   *
   * @param records Indicator data about to be unpublished
   * @returns The affected public views
   */
  publicViewsUsingData(
    records: Array<Pick<IndicatorData, 'dataLayer' | 'indicator'>>
  ): Bookmark[] {
    const pairs = new Set(
      records.map(record => `${record.dataLayer}:${record.indicator}`)
    );
    return this.bookmarkStore.asArray().filter(
      bookmark =>
        bookmark.published &&
        !bookmark.private &&
        Array.isArray(bookmark.config) &&
        bookmark.config.some((widget: CustomGridStackWidget) => {
          if (!widget.widget || ['text', 'teaser'].includes(widget.type)) {
            return false;
          }
          const layers = this.getDataLayerIdsFromWidget(widget.widget);
          const indicators = this.getIndicatorIdsFromWidget(widget.widget);
          return layers.some(layer =>
            indicators.some(indicator => pairs.has(`${layer}:${indicator}`))
          );
        })
    );
  }

  /**
   * Checks a view before it gets published (server validation of the view as
   * it would be saved) and, if the public would see problems, asks whether
   * to publish anyway. A failing validation never blocks publishing.
   *
   * @param bookmark The view with the values about to be saved
   * @returns true if publishing should go ahead
   */
  async confirmPublication(bookmark: Partial<Bookmark>): Promise<boolean> {
    const title = bookmark.title || 'Ansicht';
    let result: BookmarkValidationResult;
    try {
      result = await this.bookmarkService.validateDraft({
        ...bookmark,
        published: true,
        private: false,
      });
    } catch (error) {
      console.error('Validation before publishing failed', error);
      return true;
    }
    // unpublished topics the view is filed under: asked about separately,
    // they can be published along with the view
    const topicIssues = this.canPublishTopics()
      ? result.issues.filter(issue => issue.details.topicMembership)
      : [];
    let topicIdsToPublish: string[] = [];
    if (topicIssues.length) {
      const choice = await this.confirmationDialogService.confirmPublishTopics(
        title,
        topicIssues.map(issue => issue.details.referenceName || '')
      );
      if (choice === 'cancel') return false;
      if (choice === 'withTopics') {
        topicIdsToPublish = topicIssues.map(
          issue => issue.details.referenceId || ''
        );
      }
    }

    // the messages name the indicator/year themselves; the same problem in
    // several widgets is listed once
    const problems = result.issues
      .filter(
        issue => issue.severity !== 'info' && !topicIssues.includes(issue)
      )
      .map(issue => issue.message);
    if (
      problems.length > 0 &&
      !(await this.confirmPublishWithProblems(bookmark, title, problems))
    ) {
      return false;
    }
    this.publishTopics(topicIdsToPublish);
    return true;
  }

  /**
   * "Trotzdem veröffentlichen?" for the problems of a view about to be
   * published - with a way out to its details where available.
   *
   * @returns true if publishing should go ahead
   */
  private async confirmPublishWithProblems(
    bookmark: Partial<Bookmark>,
    title: string,
    problems: string[]
  ): Promise<boolean> {
    const uniqueProblems = [...new Set(problems)];
    // a view not saved yet has no details ("Nutzung & Freigabe") to show;
    // the details are a beta feature (feature flag bookmarkInventory)
    if (!bookmark._id || !this.activeUserStore.canUseBookmarkInventory()) {
      return this.confirmationDialogService.confirmPublishWithIssues(
        title,
        uniqueProblems
      );
    }
    const choice =
      await this.confirmationDialogService.confirmPublishWithIssuesOrDetails(
        title,
        uniqueProblems
      );
    if (choice === 'details') {
      this.openInventory(bookmark._id);
      return false;
    }
    return choice === 'publish';
  }

  /**
   * Publishing topics needs write rights: not read-only (level 10) and not
   * workspace-based (level 0) - the same rule as "Fehlendes freigeben"
   * (BookmarkInventoryPanelComponent#canPublish); the server checks again.
   */
  private canPublishTopics(): boolean {
    const user = this.activeUserStore.user();
    if (!user || this.activeUserStore.isReadOnly()) return false;
    return !!user.isAdmin || !!user.isSuperAdmin || user.level > 0;
  }

  /** Publishes the given (loaded) topics. */
  private publishTopics(topicIds: string[]): void {
    const topics = topicIds
      .map(id => this.topicStore.items()[id])
      .filter(topic => !!topic)
      .map(topic => ({ ...topic, published: true }));
    if (topics.length) this.topicStore.updateMultiple({ items: topics });
  }

  /**
   * Validates all views of the active commune: references, publication and
   * data availability on the server (against the database - it can tell
   * deleted, missing and foreign references apart), the access checks for
   * the current user in the client. Falls back to the client-only
   * validation if the server can't be reached.
   *
   * @returns Summary in the shape the validation panel expects
   */
  async validateBookmarksWithServer(): Promise<BookmarkValidationSummary> {
    const bookmarks = this.bookmarkStore.asArray();
    let serverResults: BookmarkValidationResult[];
    try {
      serverResults = await this.bookmarkService.validateByCommune(
        this.activeUserStore.commune()
      );
    } catch (error) {
      console.error('Server validation failed, using client validation', error);
      return this.validateBookmarksWithAccess(bookmarks);
    }

    // only views the user can see in the lists - the server already leaves
    // out foreign private views, this keeps the panel in line with the store
    const knownIds = new Set(bookmarks.map(bookmark => bookmark._id));
    serverResults = serverResults.filter(result =>
      knownIds.has(result.bookmarkId)
    );

    const isDataIssue = (issue: BookmarkValidationIssue) =>
      issue.issueType === 'no-data-available' ||
      issue.issueType === 'no-data-configured';
    const toResult = (
      result: BookmarkValidationResult,
      issues: BookmarkValidationIssue[]
    ): BookmarkValidationResult => {
      const errors = issues.filter(issue => !isWarningIssue(issue));
      return {
        ...result,
        issues,
        isValid: errors.length === 0,
        hasWarnings: errors.length < issues.length,
      };
    };

    const referenceResults = serverResults.map(result =>
      toResult(
        result,
        result.issues.filter(issue => !isDataIssue(issue))
      )
    );
    const dataResults = serverResults.map(result =>
      toResult(result, result.issues.filter(isDataIssue))
    );

    // "not in my store" only means "no access" if the server doesn't know
    // the reference as missing, deleted or foreign
    const brokenIdsByBookmark = new Map<string, Set<string>>();
    referenceResults.forEach(result => {
      const brokenIds = new Set<string>();
      result.issues
        .filter(issue =>
          /^(missing|deleted|foreign-commune)-/.test(issue.issueType)
        )
        .forEach(issue => {
          if (issue.details.dataLayerId)
            brokenIds.add(issue.details.dataLayerId);
          if (issue.details.indicatorId)
            brokenIds.add(issue.details.indicatorId);
        });
      brokenIdsByBookmark.set(result.bookmarkId, brokenIds);
    });
    const accessResults = bookmarks.map(bookmark => {
      const result = this.validateBookmarkAccess(bookmark);
      const brokenIds = brokenIdsByBookmark.get(bookmark._id) || new Set();
      const issues = result.issues.filter(
        issue =>
          !brokenIds.has(issue.details.dataLayerId || '') &&
          !brokenIds.has(issue.details.indicatorId || '')
      );
      return toResult(result, issues);
    });

    const summarize = (results: BookmarkValidationResult[]) => ({
      totalBookmarks: results.length,
      validBookmarks: results.filter(r => r.isValid).length,
      invalidBookmarks: results.filter(r => !r.isValid).length,
      warningBookmarks: results.filter(r => r.isValid && r.hasWarnings).length,
      totalIssues: results.reduce((sum, r) => sum + r.issues.length, 0),
      results,
    });

    return {
      ...summarize(
        this.markIssuesCausedByAccess(referenceResults, accessResults)
      ),
      validatedAt: new Date(),
      accessValidation: summarize(accessResults),
      dataValidation: summarize(dataResults),
    };
  }

  /**
   * Validates a single bookmark for workspace access issues
   * Checks if the current user has access to all data referenced in the bookmark
   */
  private validateBookmarkAccess(bookmark: Bookmark): BookmarkValidationResult {
    const issues: BookmarkValidationIssue[] = [];
    const user = this.activeUserStore.user();

    // Skip validation for users the server doesn't restrict: admins, and
    // users with a global level but no workspaces (permission-helper.js
    // #isAdminOrNoWorkspaces) - everyone else may be workspace-filtered
    const hasWorkspaces = !!user?.workspaces?.length;
    if (
      !user ||
      user.isAdmin ||
      user.isSuperAdmin ||
      (user.level > 0 && !hasWorkspaces)
    ) {
      return {
        bookmarkId: bookmark._id,
        bookmarkTitle: bookmark.title,
        isValid: true,
        issues: [],
        validatedAt: new Date(),
      };
    }

    // Check each widget in the bookmark configuration
    if (bookmark.config && Array.isArray(bookmark.config)) {
      bookmark.config.forEach((widget: CustomGridStackWidget) => {
        const widgetIssues = this.validateWidgetAccess(widget, bookmark);
        issues.push(...widgetIssues);
      });
    }

    return {
      bookmarkId: bookmark._id,
      bookmarkTitle: bookmark.title,
      isValid: issues.length === 0,
      issues,
      validatedAt: new Date(),
    };
  }

  /**
   * Validates a single widget for workspace access issues
   * Uses the same logic as the public validation but WITHOUT published checks
   */
  private validateWidgetAccess(
    widget: CustomGridStackWidget,
    bookmark: Bookmark
  ): BookmarkValidationIssue[] {
    const issues: BookmarkValidationIssue[] = [];

    // Skip content widgets (info widgets still show indicators)
    if (!widget.widget || ['text', 'teaser'].includes(widget.type)) {
      return issues;
    }

    const widgetConfig = widget.widget;
    const dataLayerIds =
      widget.type === 'info'
        ? []
        : this.getDataLayerIdsFromWidget(widgetConfig);
    const indicatorIds = this.getIndicatorIdsFromWidget(widgetConfig);

    // Data layers and indicators are checked independently (previously
    // indicators were only checked inside the data layer loop - never for
    // widgets without a data layer, and once per data layer otherwise).
    // Not in the store = not visible to the current user; deleted/missing
    // references are filtered out via the server validation.
    dataLayerIds
      .filter(dataLayerId => !this.dataLayerStore.getById(dataLayerId))
      .forEach(dataLayerId =>
        issues.push(
          this.createAccessIssue(
            bookmark,
            widget,
            'workspace-restricted-datalayer',
            undefined,
            undefined,
            dataLayerId
          )
        )
      );

    indicatorIds
      .filter(indicatorId => !this.indicatorStore.getById(indicatorId))
      .forEach(indicatorId =>
        issues.push(
          this.createAccessIssue(
            bookmark,
            widget,
            'workspace-restricted-indicator',
            undefined,
            undefined,
            indicatorId
          )
        )
      );

    return issues;
  }

  /**
   * Creates an access-related validation issue object
   */
  private createAccessIssue(
    bookmark: Bookmark,
    widget: CustomGridStackWidget,
    issueType: BookmarkValidationIssue['issueType'],
    dataLayer?: DataLayer,
    indicator?: Indicator,
    missingId?: string
  ): BookmarkValidationIssue {
    let message = '';

    switch (issueType) {
      case 'workspace-restricted-datalayer':
        message = dataLayer
          ? `Jahr "${dataLayer.year}" ist nicht in Ihrem Arbeitsbereich verfügbar`
          : `Jahr mit ID "${missingId}" ist nicht in Ihrem Arbeitsbereich verfügbar`;
        break;
      case 'workspace-restricted-indicator':
        message = indicator
          ? `Indikator "${indicator.name}" ist nicht in Ihrem Arbeitsbereich verfügbar`
          : `Indikator mit ID "${missingId}" ist nicht in Ihrem Arbeitsbereich verfügbar`;
        break;
      case 'permission-denied-data':
        message = `Sie haben keine Berechtigung für diese Daten`;
        break;
    }

    // Generate widget title
    const widgetTitle = this.getWidgetTitle(widget, indicator);

    return {
      bookmarkId: bookmark._id,
      bookmarkTitle: bookmark.title,
      issueType,
      widgetId: String(widget.id),
      widgetType: widget.type,
      widgetHeadline: widgetTitle,
      details: {
        dataLayerId: dataLayer?._id || missingId,
        dataLayerName: dataLayer?.year
          ? String(dataLayer.year)
          : missingId || 'Unbekannt',
        indicatorId: indicator?._id || missingId,
        indicatorName: indicator?.name || missingId || 'Unbekannt',
      },
      message,
    };
  }

  /**
   * Validates a single bookmark for data availability issues
   * Checks if widgets have actual indicator data (regardless of publication status)
   * This validation applies to ALL bookmarks (public and private)
   */
  private validateBookmarkDataAvailability(
    bookmark: Bookmark
  ): BookmarkValidationResult {
    const issues: BookmarkValidationIssue[] = [];

    // Check each widget in the bookmark configuration
    if (bookmark.config && Array.isArray(bookmark.config)) {
      bookmark.config.forEach((widget: CustomGridStackWidget) => {
        const widgetIssues = this.validateWidgetDataAvailability(
          widget,
          bookmark
        );
        issues.push(...widgetIssues);
      });
    }

    return {
      bookmarkId: bookmark._id,
      bookmarkTitle: bookmark.title,
      isValid: issues.length === 0,
      issues,
      validatedAt: new Date(),
    };
  }

  /**
   * Validates a single widget for data availability
   * Checks if the widget configuration will display any data
   */
  private validateWidgetDataAvailability(
    widget: CustomGridStackWidget,
    bookmark: Bookmark
  ): BookmarkValidationIssue[] {
    const issues: BookmarkValidationIssue[] = [];

    // Skip content widgets (text, teaser, info) or is external widget-source
    if (
      !widget.widget ||
      ['text', 'teaser', 'info'].includes(widget.type) ||
      widget.widget.context === 'external'
    ) {
      return issues;
    }

    const widgetConfig = widget.widget;
    const focusProperty = widgetConfig.focusProperty;
    const indicatorId = widgetConfig.indicator;
    const dataLayerId = widgetConfig.dataLayer;
    const indicatorIds = this.getIndicatorIdsFromWidget(widgetConfig);
    const dataLayerIds = this.getDataLayerIdsFromWidget(widgetConfig);
    const indicator = indicatorId
      ? this.indicatorStore.getById(indicatorId)
      : undefined;
    const dataLayer = dataLayerId
      ? this.dataLayerStore.getById(dataLayerId)
      : undefined;

    // Handle different focus properties
    if (focusProperty === 'indicator') {
      // Check if indicator has data in any of the dataLayers

      if (!indicatorId) {
        // No indicator configured = no data
        issues.push(
          this.createDataIssue(
            bookmark,
            widget,
            'no-data-configured',
            'Kein Indikator konfiguriert',
            undefined,
            widgetConfig.indicators
          )
        );
        return issues;
      }

      // Check if ANY of the dataLayers has data for this indicator
      const hasDataInAnyLayer = dataLayerIds.some(dataLayerId =>
        this.indicatorDataStore.dataLayerAndIndicatorHasData(
          dataLayerId,
          indicatorId
        )
      );

      if (!hasDataInAnyLayer) {
        issues.push(
          this.createDataIssue(
            bookmark,
            widget,
            'no-data-available',
            `Indikator "${indicator?.name || indicatorId}" hat keine Daten in den konfigurierten Jahren`,
            indicator,
            undefined,
            dataLayer,
            dataLayerIds
          )
        );
      }
    } else if (focusProperty === 'dataLayer') {
      // Check if dataLayer has data for any of the indicators

      if (!dataLayerId) {
        // No dataLayer configured = no data
        issues.push(
          this.createDataIssue(
            bookmark,
            widget,
            'no-data-configured',
            'Kein Jahr konfiguriert'
          )
        );
        return issues;
      }

      // Check if ANY of the indicators has data in this dataLayer
      const indicatorIdsWithNoData = indicatorIds.filter(
        indicatorId =>
          !this.indicatorDataStore.dataLayerAndIndicatorHasData(
            dataLayerId,
            indicatorId
          )
      );

      if (indicatorIdsWithNoData.length === indicatorIds.length) {
        issues.push(
          this.createDataIssue(
            bookmark,
            widget,
            'no-data-available',
            `Jahr "${dataLayer?.year || dataLayerId}" hat keine Daten für die konfigurierten Indikatoren`,
            undefined,
            indicatorIdsWithNoData,
            dataLayer
          )
        );
      }
    } else if (focusProperty === 'feature') {
      if (indicatorIds.length === 0 || dataLayerIds.length === 0) {
        issues.push(
          this.createDataIssue(
            bookmark,
            widget,
            'no-data-configured',
            'Keine Indikatoren oder Jahre konfiguriert'
          )
        );
        return issues;
      }

      // Check if there's ANY data for the combination
      let hasAnyData = false;
      for (const dataLayerId of dataLayerIds) {
        for (const indicatorId of indicatorIds) {
          if (
            this.indicatorDataStore.dataLayerAndIndicatorHasData(
              dataLayerId,
              indicatorId
            )
          ) {
            hasAnyData = true;
            break;
          }
        }
        if (hasAnyData) break;
      }

      if (!hasAnyData) {
        issues.push(
          this.createDataIssue(
            bookmark,
            widget,
            'no-data-available',
            'Keine Daten für die Kombination aus Indikatoren und Jahren verfügbar',
            undefined,
            indicatorIds,
            undefined,
            dataLayerIds
          )
        );
      }
    }

    return issues;
  }

  /**
   * Cross-references validation issues with access issues.
   * Marks issues as causedByAccessIssue when the same restricted
   * dataLayer or indicator ID appears in the access results for that bookmark.
   */
  private markIssuesCausedByAccess(
    publicResults: BookmarkValidationResult[],
    accessResults: BookmarkValidationResult[]
  ): BookmarkValidationResult[] {
    // Build map: bookmarkId -> Set of all resource IDs blocked by access issues
    const restrictedByBookmark = new Map<string, Set<string>>();

    for (const accessResult of accessResults) {
      if (accessResult.isValid) continue;

      const restrictedIds = new Set<string>();
      for (const issue of accessResult.issues) {
        if (issue.details.dataLayerId) {
          restrictedIds.add(issue.details.dataLayerId);
        }
        if (issue.details.indicatorId) {
          restrictedIds.add(issue.details.indicatorId);
        }
      }
      restrictedByBookmark.set(accessResult.bookmarkId, restrictedIds);
    }

    return publicResults.map(result => {
      const restrictedIds = restrictedByBookmark.get(result.bookmarkId);
      if (!restrictedIds) return result;

      const annotatedIssues = result.issues.map(issue => {
        const isCausedByAccess =
          (!!issue.details.dataLayerId &&
            restrictedIds.has(issue.details.dataLayerId)) ||
          (!!issue.details.indicatorId &&
            restrictedIds.has(issue.details.indicatorId)) ||
          (issue.details.dataLayerIds?.some(id => restrictedIds.has(id)) ??
            false) ||
          (issue.details.indicatorIds?.some(id => restrictedIds.has(id)) ??
            false);

        return isCausedByAccess
          ? { ...issue, causedByAccessIssue: true }
          : issue;
      });

      return { ...result, issues: annotatedIssues };
    });
  }

  /**
   * Creates a data availability issue object
   */
  private createDataIssue(
    bookmark: Bookmark,
    widget: CustomGridStackWidget,
    issueType: 'no-data-configured' | 'no-data-available',
    message: string,
    indicator?: Indicator,
    indicatorIds?: string[],
    dataLayer?: DataLayer,
    dataLayerIds?: string[]
  ): BookmarkValidationIssue {
    const widgetTitle = this.getWidgetTitle(widget, indicator);

    return {
      bookmarkId: bookmark._id,
      bookmarkTitle: bookmark.title,
      issueType,
      widgetId: String(widget.id),
      widgetType: widget.type,
      widgetHeadline: widgetTitle,
      details: {
        dataLayerId: dataLayer?._id,
        dataLayerIds:
          dataLayerIds && dataLayerIds.length ? dataLayerIds : undefined,
        dataLayerName: dataLayer?.year ? String(dataLayer.year) : undefined,
        indicatorId: indicator?._id,
        indicatorIds:
          indicatorIds && indicatorIds.length ? indicatorIds : undefined,
        indicatorName: indicator?.name,
      },
      message,
    };
  }
}
